'use client';

import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Crown, Check } from 'lucide-react';
import { SUBSCRIPTIONS } from '@/lib/site-config';

interface BilingualSubscriptionGateProps {
  isAuthenticated: boolean;
  currentPath?: string;
}

export default function BilingualSubscriptionGate({ isAuthenticated, currentPath }: BilingualSubscriptionGateProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const isAuth = !!session;

  const handleSignIn = () => {
    const returnUrl = currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
    router.push(`/login?callbackUrl=${encodeURIComponent(returnUrl)}`);
  };

  const handleSubscribe = async (planId: string) => {
    if (!isAuth) {
      handleSignIn();
      return;
    }

    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId }),
      });

      if (!res.ok) throw new Error('Checkout failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const amayaPro = SUBSCRIPTIONS.find(s => s.id === 'amaya_pro');

  return (
    <>
      {/* Semi-Transparent Background - Show the book behind for marketing! */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)', /* Much more transparent - show the book! */
          backdropFilter: 'blur(12px)', /* Subtle blur to make modal stand out */
          WebkitBackdropFilter: 'blur(12px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            background: 'linear-gradient(135deg, rgba(26, 11, 46, 0.98) 0%, rgba(15, 5, 32, 0.98) 100%)', /* Slightly more opaque for readability */
            backdropFilter: 'blur(20px)', /* Extra blur on modal itself */
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)', /* Stronger shadow to stand out */
            border: '1px solid rgba(255, 255, 255, 0.15)', /* Slightly brighter border */
            position: 'relative',
          }}
        >
          {/* Lock Icon */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(167, 139, 250, 0.4)',
              }}
            >
              <Crown size={40} color="white" />
            </div>
            <h2
              style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 900,
                color: 'white',
                margin: '0 0 12px 0',
                lineHeight: 1.2,
              }}
            >
              {isAuth ? 'Upgrade to Amaya Pro' : 'Sign In First, Then Subscribe'}
            </h2>
            <p
              style={{
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                color: 'rgba(255, 255, 255, 0.8)',
                margin: 0,
              }}
            >
              {isAuth
                ? 'Bilingual audio books require an Amaya Pro subscription'
                : 'Sign in to watch free stories or subscribe to Amaya Pro for bilingual books'}
            </p>
          </div>

          {!isAuth && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <button
                onClick={handleSignIn}
                style={{
                  padding: '16px 48px',
                  background: 'white',
                  color: '#1a0b2e',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 4px 24px rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 255, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(255, 255, 255, 0.3)';
                }}
              >
                Sign In
              </button>
              <div
                style={{
                  margin: '20px 0',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                OR
              </div>
            </div>
          )}

          {/* Amaya Pro Plan */}
          {amayaPro && (
            <div
              style={{
                padding: '32px 24px',
                background: 'rgba(167, 139, 250, 0.15)',
                border: '2px solid rgba(167, 139, 250, 0.5)',
                borderRadius: '16px',
                position: 'relative',
                transition: 'all 0.3s ease',
                marginBottom: '24px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.8)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(167, 139, 250, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.5)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '20px',
                  padding: '6px 16px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Required
              </div>
              <div style={{ marginBottom: '16px' }}>
                <h3
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 900,
                    color: 'white',
                    margin: '0 0 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Crown size={24} color="#a78bfa" />
                  Amaya Pro
                </h3>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: '#a78bfa' }}>
                  ${(amayaPro.priceCents / 100).toFixed(2)}
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.6)' }}>
                    /month
                  </span>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                {amayaPro.perks.map((perk, index) => (
                  <li
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.9375rem',
                    }}
                  >
                    <Check size={18} color="#a78bfa" />
                    {perk}
                  </li>
                ))}
              </ul>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubscribe('amaya_pro');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Choose Amaya Pro
              </button>
            </div>
          )}

          {/* Close/Back Button */}
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => router.back()}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

